import { and, anyPass, contains, isEmpty, isNil, not } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router';
import { Source } from '../../../constants/Source';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import { isBoardId } from '../../media-manager-sdk/boards';
import {
  isAllSource,
  isBoards as isBoardsSource,
  isFavorites as isFavoritesSource,
  isGooglePhotos as isGooglePhotosChecker,
  isHome as isHomeSource,
  isMediaFromWix as isMediaFromWixSource,
  isNTT as isNTTSourceChecker,
  isYell as isYellSourceChecker,
  isRecommended as isRecommendedSource,
  isShutterstockPicture as isShutterstockPictureSource,
  isShutterstockVideo as isShutterstockVideoSource,
  isSiteFiles as isSiteFilesSource,
  isTrash as isTrashSource,
  isUnsplash as isUnsplashSourceChecker,
  isVisitorsUploads as isVisitorsUploadsSource,
  isWixSite as isWixSiteSourceChecker,
  isEnterprise as isEnterpriseSourceChecker,
} from '../../media-manager-sdk/sources';
import { isAllNamespace } from '../../mmgrSettings/namespace';
import { RouteMatcher } from '../../RouteHelpers';
import { ISourcesFlags, IUseSourcesOuter } from './useSources.types';

export const useSources = ({ source, searchQuery = '' }: IUseSourcesOuter) => {
  const { mmgrSettings } = useMMGRSettings();
  const {
    externalSources,
    hasVisitorUploads,
    namespace,
    tabsList,
    isYellExperimentEnabled,
    isForceYellExperimentEnabled,
    isNTTExperimentEnabled,
  } = mmgrSettings;

  const { pathname } = useLocation();
  const { folderId = '' } = useParams<{ folderId: string }>();

  const sourcesFlags: ISourcesFlags = useMemo(() => {
    const isAllMediaSource = isAllSource(source);
    const isSiteFiles = isSiteFilesSource(source);
    const isVisitorsUploads = isVisitorsUploadsSource(source);
    const isFavorites = isFavoritesSource(source);
    const isTrash = isTrashSource(source);

    const isShutterstockPicture = isShutterstockPictureSource(source);
    const isShutterstockVideo = isShutterstockVideoSource(source);
    const isShutterstock = isShutterstockPicture || isShutterstockVideo;

    const isHome = isHomeSource(source);
    const isBoards = isBoardsSource(source);
    const isUnsplash = isUnsplashSourceChecker(source);
    const isNTT = isNTTSourceChecker(source);
    const isYell = isYellSourceChecker(source);
    const isEnterprise = isEnterpriseSourceChecker(source);

    const isMediaFromWix = isMediaFromWixSource(source);
    const isRecommended = isRecommendedSource(source);
    const isShowAll = isAllNamespace(namespace);
    const isPublicMediaLike = isEnterprise || isMediaFromWix || isYell || isNTT;

    const isWixSitesSource = isWixSiteSourceChecker(source);
    const isGooglePhotosSource = isGooglePhotosChecker(source);

    const isBoardRoot = isBoards && isBoardId(folderId);
    const isBoardsRoot = RouteMatcher.isBoardsRoot(pathname);
    const isRootAddFilesToBoardModalPath = RouteMatcher.isAddFilesToBoardModal(pathname);

    const isSearch =
      RouteMatcher.isSearch(pathname) ||
      and(isWixSitesSource || isRootAddFilesToBoardModalPath, not(anyPass([isEmpty, isNil])(searchQuery)));

    const isManageableMedia = isSiteFiles || isVisitorsUploads || isFavorites || isTrash || isBoards;
    const isExplorableMedia = isPublicMediaLike || isShutterstock || isRecommended || isHome || isUnsplash;

    return {
      isAllMediaSource,
      isSiteFiles,
      isVisitorsUploads,
      isFavorites,
      isTrash,
      isSearch,
      isNTT,
      isYell,

      isManageableMedia,
      isExplorableMedia,
      isRecommended,
      isMediaFromWix,
      isPublicMediaLike,
      isEnterprise,
      isShutterstockPicture,
      isShutterstockVideo,
      isShutterstock,
      isUnsplash,
      isShowAll,
      isHome,
      isBoards,

      isGooglePhotosSource,
      isWixSitesSource,
      isBoardRoot,
      isBoardsRoot,
    };
  }, [source, namespace, searchQuery, pathname]);

  const isSourceEnabled = useCallback(
    (source: Source) => {
      if (isVisitorsUploadsSource(source) && !hasVisitorUploads) {
        return false;
      }
      if (isNTTSourceChecker(source) && !window.isNTT && !isNTTExperimentEnabled) {
        return false;
      }
      if (isYellSourceChecker(source)) {
        return isForceYellExperimentEnabled || (isYellExperimentEnabled && window.isYellEnabled);
      }

      return contains(source, tabsList) || contains(source, externalSources);
    },
    [hasVisitorUploads, tabsList, externalSources],
  );

  return useMemo(() => ({ ...sourcesFlags, isSourceEnabled }), [sourcesFlags, isSourceEnabled]);
};
